import { EventType, type StreamChunk, type UIMessage } from '@tanstack/ai/client';
import type { ConnectConnectionAdapter } from '@tanstack/ai-react';
import { encode } from '@toon-format/toon';
import { type EventSourceMessage, EventSourceParserStream } from 'eventsource-parser/stream';
import { getMessageText } from './utils';

export type PendingAction = {
  actionId: string;
  tool: string;
  summary: string;
};

type ConnectionDeps = {
  getAccessToken: () => Promise<string>;
  getContext: () => unknown;
  onPendingActions: (actions: PendingAction[]) => void;
};

// Hex SHA-256 of the request body. CloudFront OAC signs requests with SigV4 but not POST bodies,
// so the AWS_IAM function URL rejects the signature unless this header carries the body hash.
const sha256Hex = async (body: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

// Manual reader rather than `for await`: Safari doesn't implement ReadableStream async iteration.
// The finally cancels on early exit (consumer `break`, or a JSON.parse throw downstream).
async function* readSseEvents(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<EventSourceMessage> {
  const reader = body
    // TextDecoderStream's writable is typed WritableStream<BufferSource>; narrow to satisfy pipeThrough.
    .pipeThrough(new TextDecoderStream() as ReadableWritablePair<string, Uint8Array>)
    .pipeThrough(new EventSourceParserStream())
    .getReader();
  try {
    for (let r = await reader.read(); !r.done; r = await reader.read()) yield r.value;
  } finally {
    await reader.cancel();
  }
}

const textStart = (messageId: string): StreamChunk =>
  ({ type: EventType.TEXT_MESSAGE_START, messageId, role: 'assistant' }) as StreamChunk;
const textDelta = (messageId: string, delta: string): StreamChunk =>
  ({ type: EventType.TEXT_MESSAGE_CONTENT, messageId, delta }) as StreamChunk;
const textEnd = (messageId: string): StreamChunk =>
  ({ type: EventType.TEXT_MESSAGE_END, messageId }) as StreamChunk;

type SseOutcome =
  | { kind: 'delta'; text: string }
  | { kind: 'result'; answer?: string; pendingActions: PendingAction[] }
  | { kind: 'error'; message: string }
  | { kind: 'ignore' };

// Classifies one SSE event into what the run should do; `ping` and empty tokens map to `ignore`.
const classifyEvent = (ev: EventSourceMessage): SseOutcome => {
  if (ev.event === 'token') {
    const { text } = JSON.parse(ev.data) as { text?: string };
    return text ? { kind: 'delta', text } : { kind: 'ignore' };
  }
  if (ev.event === 'result') {
    const parsed = JSON.parse(ev.data) as { answer?: string; pendingActions?: PendingAction[] };
    return { kind: 'result', answer: parsed.answer, pendingActions: parsed.pendingActions ?? [] };
  }
  if (ev.event === 'error') {
    const { error } = JSON.parse(ev.data) as { error?: string };
    return { kind: 'error', message: error ?? 'Assistant error.' };
  }
  return { kind: 'ignore' };
};

/**
 * Translates the SSE stream into an AG-UI TEXT_MESSAGE_* sequence. TEXT_MESSAGE_START is deferred to
 * the first content so an error before any token can't leave a dangling, never-closed message.
 * Returns the terminal `result` event's actions; its `answer` is emitted whole if nothing streamed.
 */
async function* streamAnswer(
  body: ReadableStream<Uint8Array>,
  messageId: string
): AsyncGenerator<StreamChunk, PendingAction[]> {
  let started = false;
  let result: { pendingActions: PendingAction[] } | null = null;

  // Opens the message on first use; a no-op thereafter so the deferred start happens exactly once.
  const open = function* (): Generator<StreamChunk> {
    if (started) return;
    started = true;
    yield textStart(messageId);
  };

  for await (const ev of readSseEvents(body)) {
    const outcome = classifyEvent(ev);
    if (outcome.kind === 'error') {
      if (started) yield textEnd(messageId);
      throw new Error(outcome.message);
    }
    if (outcome.kind === 'delta') {
      yield* open();
      yield textDelta(messageId, outcome.text);
    }
    if (outcome.kind === 'result') {
      result = { pendingActions: outcome.pendingActions };
      if (!started && outcome.answer) {
        yield* open();
        yield textDelta(messageId, outcome.answer);
      }
    }
  }

  if (started) yield textEnd(messageId);
  // The stream ended without a terminal `result`: the answer is truncated and any pending actions are
  // lost, so fail rather than presenting a partial reply as complete (the CLI rejects this too).
  if (!result) throw new Error('The assistant response was cut short. Please try again.');
  return result.pendingActions;
}

/**
 * Connection adapter for the `/api/chat` backend. Proposed actions are handed to the caller
 * out-of-band via `onPendingActions`.
 *
 * CloudFront OAC fronts the function URL: it overwrites `Authorization` with its SigV4 signature
 * (so the JWT travels in `x-emma-authorization`) and signs the request but not the POST body
 * (so the body SHA256 must be supplied in `x-amz-content-sha256`).
 */
export const createChatConnection = ({
  getAccessToken,
  getContext,
  onPendingActions,
}: ConnectionDeps): ConnectConnectionAdapter => {
  return {
    async *connect(messages, _data, abortSignal, runContext) {
      const token = await getAccessToken();
      const history = (messages as UIMessage[]).map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: getMessageText(m.parts),
      }));
      const last = history.at(-1);
      const pageContext = getContext();

      let context: string | undefined;
      try {
        context = pageContext ? encode(pageContext) : undefined;
      } catch {
        // Best-effort: drop context if it can't be serialized rather than failing the request.
        context = undefined;
      }

      const body = JSON.stringify({
        message: last?.content,
        history: history.length > 1 ? history.slice(0, -1) : undefined,
        context,
      });

      const response = await fetch(`${import.meta.env.VITE_ASSISTANT_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          // CloudFront OAC overwrites Authorization with its SigV4 signature; the JWT travels in
          // a custom header the app reads instead. Authorization is kept for local/direct calls.
          Authorization: `Bearer ${token}`,
          'x-emma-authorization': `Bearer ${token}`,
          'x-amz-content-sha256': await sha256Hex(body),
        },
        body,
        signal: abortSignal,
      });

      if (!response.ok || !response.body) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(errorData.error ?? `Request failed (${response.status})`);
      }

      const { runId, threadId } = runContext;
      const messageId = crypto.randomUUID();

      yield { type: EventType.RUN_STARTED, threadId, runId } as StreamChunk;

      const pendingActions = yield* streamAnswer(response.body, messageId);

      // Emit before RUN_FINISHED: a consumer that stops iterating on RUN_FINISHED would otherwise
      // never pull the chunk after which this ran. Guard so a callback throw can't break the stream.
      try {
        onPendingActions(pendingActions);
      } catch (error) {
        // Best-effort: surfacing pending actions must not fail the run, but report so a
        // callback bug doesn't silently swallow approvals.
        console.error('Failed to surface assistant pending actions:', error);
      }

      yield { type: EventType.RUN_FINISHED, threadId, runId } as StreamChunk;
    },
  };
};
